<template>
  <v-card>
    <v-card-text>
      <v-container>
        <ApiError :errors="errors"></ApiError>
        <div class="text-center pb-5" v-if="isUploading">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <v-stepper v-model="currentStep">
          <v-stepper-header>
            <v-stepper-step :complete="currentStep > 1" step="1">Setup</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="currentStep > 2" :editable="canOpenStep2" step="2">
              Upload & Review
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :editable="showSummary" step="3">Result</v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <v-stepper-content step="1">
              <v-row>
                <v-col cols="12" md="6">
                  <h3 class="mb-2">{{ $i18n.translate("Select Audience") }}</h3>
                  <v-radio-group v-model="payoutType" class="mt-0" :rules="rules.payoutType">
                    <v-radio label="Participant" value="PARTICIPANT"></v-radio>
                    <v-radio :label="$i18n.translate('Organization')" value="ORGANIZATION"></v-radio>
                  </v-radio-group>

                  <ParticipantTypeField
                    v-if="payoutType === 'PARTICIPANT'"
                    showKey
                    dense
                    outlined
                    class="required"
                    v-model="selectedParticipantType"
                    :label="$i18n.translate('Participant Type')"
                    status="Active"
                    :allowAdjustments="true"
                    :rules="rules.participantType"
                  />

                  <OrganizationTypeField
                    v-if="payoutType === 'ORGANIZATION'"
                    dense
                    outlined
                    class="required"
                    v-model="selectedOrganizationType"
                    :label="$i18n.translate('Organization Type')"
                    status="Active"
                    :allowAdjustments="true"
                    :rules="rules.organizationType"
                  />

                  <v-alert v-if="showMissingTemplateAlert" dense outlined type="error" class="mt-3 mb-0">
                    No Bulk Payout Template is assigned to this {{ selectedTypeLabel }}. Please contact the program
                    administrator.
                  </v-alert>
                  <v-alert v-if="showInactiveTemplateAlert" dense outlined type="error" class="mt-3 mb-0">
                    The Bulk Payout Template assigned to this {{ selectedTypeLabel }} is inactive. Please contact the
                    program administrator.
                  </v-alert>

                  <h3 class="mb-2 mt-4">Award Vehicle</h3>
                  <AwardVehicleField
                    outlined
                    dense
                    v-model="selectedAwardVehicle"
                    label="Select Award Vehicle"
                    :available="availableAwardVehicles"
                    class="required"
                    :rules="rules.awardVehicle"
                    :disabled="!canSelectAwardVehicle"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <h3>Instructions</h3>
                  <ol class="pl-4">
                    <li class="mb-2">
                      Select Participant or {{ $i18n.translate("Organization") }}, then select the Participant Type.
                    </li>
                    <li class="mb-2">The Award Vehicle will auto-populate.</li>
                    <li class="mb-2">Expand the Template section to view the Required Information.</li>
                    <li class="mb-2">Download the Template.</li>
                    <li class="mb-2">Complete the Template with all Required Information.</li>
                    <li class="mb-2">Click CONTINUE to upload the completed file.</li>
                  </ol>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <h3>Template</h3>

                  <p class="mb-3">
                    Download the file for the selected template, or continue if you already have a completed file.
                  </p>

                  <v-expansion-panels v-model="templateFieldsPanel" accordion>
                    <v-expansion-panel>
                      <v-expansion-panel-header>
                        <div>
                          <div class="font-weight-medium">
                            View template fields
                          </div>
                          <div class="text-caption text--secondary">{{ effectiveTemplateFields.length }} fields</div>
                        </div>
                        <template v-slot:actions>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon v-bind="attrs" v-on="on">mdi-chevron-down</v-icon>
                            </template>
                            <span>{{ templateFieldsPanel === 0 ? "Collapse Template" : "Expand Template" }}</span>
                          </v-tooltip>
                        </template>
                      </v-expansion-panel-header>

                      <v-expansion-panel-content>
                        <v-simple-table dense>
                          <thead>
                            <tr>
                              <th class="text-left">Field name</th>
                              <th class="text-left">Description</th>
                              <th class="text-left">Allowed values</th>
                              <th class="text-center">Required</th>
                              <th class="text-center">Part of duplication key</th>
                            </tr>
                          </thead>

                          <tbody>
                            <tr
                              v-for="field in effectiveTemplateFields"
                              :key="(field.fieldType && field.fieldType.name) + '-' + field.name"
                            >
                              <td class="font-weight-medium">
                                {{ field.name }}
                              </td>

                              <td>
                                {{ field.description || "—" }}
                              </td>

                              <td>
                                {{ field.allowedValues || "—" }}
                              </td>

                              <td class="text-center">
                                <v-icon v-if="field.required" small color="success">
                                  mdi-check
                                </v-icon>

                                <v-icon v-else small color="error">
                                  mdi-close
                                </v-icon>
                              </td>
                              <td class="text-center">
                                <v-icon v-if="field.partOfDuplicationKey" small color="success">
                                  mdi-check
                                </v-icon>

                                <v-icon v-else small color="error">
                                  mdi-close
                                </v-icon>
                              </td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-row>

              <v-row justify="end">
                <v-col cols="12" sm="auto">
                  <v-btn
                    outlined
                    color="primary"
                    :disabled="!canDownloadTemplate"
                    :loading="isDownloadingTemplate"
                    @click="downloadTemplate"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    Download
                  </v-btn>
                </v-col>

                <v-col cols="12" sm="auto">
                  <v-btn color="primary" :disabled="!canOpenStep2" @click="currentStep = 2">
                    Continue
                  </v-btn>
                </v-col>
              </v-row>
            </v-stepper-content>

            <v-stepper-content step="2">
              <v-row>
                <v-col cols="12" md="6">
                  <h3 class="mb-2">Upload completed Excel file</h3>
                  <v-file-input
                    outlined
                    dense
                    label="Upload File"
                    v-model="selectedFile"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    prepend-icon="mdi-paperclip"
                    @change="handleFileUpload($event)"
                    :disabled="!canUploadFile || isParsingFile"
                    :loading="isParsingFile"
                    class="required"
                    :rules="rules.uploadField"
                  />
                  <v-btn class="mb-2" text @click="backToSetup">Back to Setup</v-btn>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-4">
                    <h3 class="mb-2">Instructions</h3>
                    <ol class="pl-4">
                      <li class="mb-2">Upload the Completed Excel File.</li>
                      <li class="mb-2">An Invalid status indicates an error.</li>
                      <li class="mb-2">Refer to the Validation column for the error message.</li>
                      <li class="mb-2">Correct the errors and reload the file.</li>
                      <li>The Process Payouts button will remain disabled if any rows are Invalid.</li>
                    </ol>
                  </v-card>
                </v-col>
              </v-row>

              <v-row v-if="parsedRecords.length > 0">
                <v-col cols="12">
                  <h3 class="mb-2">Preview and validation</h3>
                  <div class="d-flex flex-wrap align-center mb-3">
                    <v-chip class="mr-2 mb-1">Total: {{ parsedRecords.length }}</v-chip>
                    <v-chip class="mr-2 mb-1" color="success">Valid: {{ validCount }}</v-chip>
                    <v-chip class="mb-1" color="error">Rows with errors: {{ invalidCount }}</v-chip>
                  </div>
                  <v-alert v-if="invalidCount === 0" dense outlined type="success" class="mb-4">
                    All {{ parsedRecords.length }} rows passed validation. The file is ready to be processed.
                  </v-alert>
                  <template v-else>
                    <v-alert dense outlined type="error" class="mb-4">
                      {{ invalidCount }} {{ invalidCount === 1 ? "row has" : "rows have" }} validation errors.
                    </v-alert>
                    <v-text-field
                      v-model="filter"
                      dense
                      outlined
                      hide-details
                      clearable
                      prepend-inner-icon="mdi-magnify"
                      label="Filter failed rows"
                      class="mb-3"
                    />
                    <v-data-table
                      class="bulk-payout-preview-table"
                      :headers="previewHeaders"
                      :items="failedRecords"
                      :search="filter"
                      :items-per-page="10"
                      dense
                    >
                      <template v-slot:item.status>
                        <v-chip small color="error">
                          Invalid
                        </v-chip>
                      </template>
                      <template v-slot:item.validationMessage="{ item }">
                        {{ item.validationErrors.join(", ") || "-" }}
                      </template>
                    </v-data-table>
                  </template>
                  <div class="mt-4">
                    <v-btn
                      color="primary"
                      :loading="isUploading"
                      :disabled="!canProcessPayouts || isUploading"
                      @click="processPayouts"
                    >
                      Process Payouts
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-stepper-content>

            <v-stepper-content step="3">
              <v-row v-if="showSummary">
                <v-col cols="12" md="6">
                  <h2>Bulk Payout Processing Summary</h2>
                  <ul class="mt-3 mb-6">
                    <li v-if="processResult.bulkPayoutBatchId">
                      <strong>Batch ID:</strong> #{{ processResult.bulkPayoutBatchId }}
                    </li>
                    <li><strong>Total records:</strong> {{ processResult.totalRows }}</li>
                    <li><strong>Successful:</strong> {{ processResult.successfulRows }}</li>
                    <li><strong>Failed:</strong> {{ processResult.failedRows }}</li>
                  </ul>
                  <v-btn color="primary" @click="onStartOver">Upload More Payouts</v-btn>
                </v-col>
                <v-col cols="12" md="6">
                  <h3 class="mb-2">Instructions</h3>
                  <ol class="pl-4">
                    <li class="mb-2">Reference the Bulk Payout Summary tab for file load results.</li>
                    <li class="mb-2">Review the tab for information on any Failed rows.</li>
                    <li class="mb-2">The Uploaded File will be stored on this tab under the Batch ID.</li>
                    <li class="mb-2">Download the file, correct the errors as needed, and repeat the upload process</li>
                  </ol>
                </v-col>
              </v-row>
              <v-alert v-else outlined type="info">Process the uploaded file in Step 2 to view results.</v-alert>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import AwardVehicleField from "@/gapp-components/components/fields/AwardVehicleField.vue";
import ApiError from "@/gapp-components/components/display/ApiError.vue";
import ParticipantTypeField from "@/gapp-components/components/fields/ParticipantTypeField.vue";
import OrganizationTypeField from "@/gapp-components/components/fields/OrganizationTypeField.vue";
import ApiService from "@/gapp-components/services/api.service";
import {
  buildTemplateWorkbook,
  downloadTemplateWorkbook,
  parseTemplateWorkbook,
  parseAmountValue
} from "@/gapp-components/utils/bulkAdjustmentExcel";

export default {
  name: "BulkPayout",
  components: {
    AwardVehicleField,
    ApiError,
    ParticipantTypeField,
    OrganizationTypeField
  },
  data() {
    return {
      isUploading: false,
      isDownloadingTemplate: false,
      isParsingFile: false,
      templateLoading: false,
      currentStep: 1,
      selectedFile: null,
      payoutType: null,
      selectedParticipantType: null,
      selectedOrganizationType: null,
      selectedTemplate: null,
      inactiveTemplate: false,
      selectedAwardVehicle: null,
      templateFieldsPanel: null,
      parsedRecords: [],
      filter: "",
      showSummary: false,
      processResult: {
        bulkPayoutBatchId: null,
        totalRows: 0,
        successfulRows: 0,
        failedRows: 0
      },
      errors: {},
      rules: {
        payoutType: [v => !!v || "Payout type is required."],
        participantType: [v => this.payoutType !== "PARTICIPANT" || !!v || "Participant type is required."],
        organizationType: [v => this.payoutType !== "ORGANIZATION" || !!v || "Organization type is required."],
        template: [v => !!v || "A bulk payout template is required."],
        awardVehicle: [v => !!v || "An award vehicle is required."],
        uploadField: [v => !!v || "Field is required"]
      }
    };
  },
  computed: {
    ...mapGetters(["selectedClient", "selectedProgram"]),
    selectedType() {
      return this.payoutType === "PARTICIPANT" ? this.selectedParticipantType : this.selectedOrganizationType;
    },
    selectedTypeLabel() {
      return this.payoutType === "PARTICIPANT"
        ? this.$i18n.translate("Participant Type")
        : this.$i18n.translate("Organization Type");
    },
    canSelectTemplate() {
      return !!this.payoutType && !!this.selectedType;
    },
    sortedTemplateFields() {
      return [...(this.selectedTemplate?.fields || [])].sort(
        (a, b) => Number(a.rankOrder || 0) - Number(b.rankOrder || 0)
      );
    },
    effectiveTemplateFields() {
      return this.sortedTemplateFields;
    },
    templateHeaders() {
      return this.effectiveTemplateFields.map(field => field.name).filter(Boolean);
    },
    showMissingTemplateAlert() {
      return this.canSelectTemplate && !this.templateLoading && !this.selectedTemplate && !this.inactiveTemplate;
    },
    showInactiveTemplateAlert() {
      return this.canSelectTemplate && !this.templateLoading && this.inactiveTemplate;
    },
    availableAwardVehicles() {
      return this.selectedType?.availableAwardVehicles || [];
    },
    isStep1Complete() {
      return this.canSelectTemplate && !!this.selectedTemplate;
    },
    canDownloadTemplate() {
      return this.isStep1Complete && this.templateHeaders.length > 0;
    },
    canSelectAwardVehicle() {
      return this.isStep1Complete;
    },
    canUploadFile() {
      return this.canSelectAwardVehicle && !!this.selectedAwardVehicle;
    },
    canOpenStep2() {
      return this.canUploadFile;
    },
    validCount() {
      return this.parsedRecords.filter(row => row.validationErrors.length === 0).length;
    },
    invalidCount() {
      return this.parsedRecords.length - this.validCount;
    },
    failedRecords() {
      return this.parsedRecords.filter(row => row.validationErrors.length > 0);
    },
    canProcessPayouts() {
      return this.canOpenStep2 && this.parsedRecords.length > 0 && this.invalidCount === 0;
    },
    previewHeaders() {
      const headers = [{ text: "Row", value: "rowNumber", width: "72px" }];
      this.effectiveTemplateFields.forEach(field => {
        headers.push({
          text: field.name,
          value: `values.${field.fieldType.name}`
        });
      });
      headers.push(
        { text: "Status", value: "status", width: "100px", sortable: false },
        { text: "Validation", value: "validationMessage", sortable: false }
      );
      return headers;
    }
  },
  methods: {
    getSelectableAwardVehicles(vehicles) {
      if (!vehicles?.length) return [];
      const seen = new Set();
      return vehicles.filter(vehicle => {
        if (!vehicle?.id || vehicle.effective !== true || seen.has(vehicle.id)) return false;
        seen.add(vehicle.id);
        return true;
      });
    },
    async fetchAssignedTemplate() {
      if (!this.canSelectTemplate) {
        return;
      }

      this.selectedTemplate = null;
      this.inactiveTemplate = false;
      this.templateLoading = true;

      const isParticipant = this.payoutType === "PARTICIPANT";

      const endpoint = isParticipant ? "participantTypes" : "organizationTypes";

      const id = isParticipant ? this.selectedParticipantType.id : this.selectedOrganizationType.id;

      ApiService.get(`/api/${endpoint}/byId/${id}?expands=bulkPayoutTemplate.fields`)
        .then(({ data }) => {
          const template = this.getAssignedTemplate(data);
          this.inactiveTemplate = template?.effective === false;
          this.selectedTemplate = this.inactiveTemplate ? null : template;
        })
        .catch(() => {
          console.log("Bulk Payout Template not found");
        })
        .finally(() => {
          this.templateLoading = false;
        });
    },
    getAssignedTemplate(data) {
      const template =
        data?.bulkPayoutTemplate || data?.bulkPayoutTemplates || (data?.bulkPayoutTemplateKey ? data : null);
      return Array.isArray(template) ? template[0] || null : template;
    },
    async downloadTemplate() {
      if (!this.canDownloadTemplate || this.isDownloadingTemplate) return;
      this.isDownloadingTemplate = true;
      this.errors = {};

      try {
        const headers = this.templateHeaders;
        const dropdowns = [];

        this.effectiveTemplateFields.forEach((field, index) => {
          if (field.allowedValues?.length > 0) {
            dropdowns.push({
              columnIndex: index + 1,
              keys: field.allowedValues.split("|"),
              hiddenSheetName: `hidden_list_${field.id}`
            });
          }
        });

        const workbook = buildTemplateWorkbook({ headers, dropdowns });
        await downloadTemplateWorkbook(workbook, `${this.selectedTemplate.name}-bulk-payout.xlsx`);
      } catch (error) {
        console.error("Error downloading template:", error);
        this.errors = this.$api.getErrorsFromResponse(error) || {
          message: error.message || "Unable to download Excel template."
        };
      } finally {
        this.isDownloadingTemplate = false;
      }
    },
    async handleFileUpload(event) {
      this.errors = {};
      this.parsedRecords = [];
      this.filter = "";
      const file = Array.isArray(event) ? event[0] : event;
      if (!file) {
        return;
      }
      if (!(file instanceof File) || !file.name.toLowerCase().endsWith(".xlsx")) {
        this.errors = { message: "Please upload an Excel (.xlsx) file." };
        this.selectedFile = null;
        return;
      }

      this.isParsingFile = true;
      this._seenDuplicationKeys = new Set();
      try {
        const excelRows = await parseTemplateWorkbook(file, { expectedHeaders: this.templateHeaders });
        this.parsedRecords = excelRows.map(row => this.validateParsedRow(row));
      } catch (error) {
        console.error("Error parsing Excel file:", error);
        this.errors = { message: error.message || "Unable to parse Excel file." };
        this.selectedFile = null;
      } finally {
        this.isParsingFile = false;
      }
    },
    validateParsedRow(row) {
      const values = {};
      const validationErrors = [];
      const seenDuplicationKeys = this._seenDuplicationKeys || (this._seenDuplicationKeys = new Set());

      this.effectiveTemplateFields.forEach(field => {
        const fieldType = field.fieldType.name;
        const rawValue = row.cells[field.name];
        let value = rawValue == null ? "" : String(rawValue).trim();
        if (fieldType === "PAYOUT_AMOUNT" && value) {
          const amount = parseAmountValue(value);
          value = amount == null ? value : String(amount);
          if (amount == null || amount <= 0) {
            validationErrors.push(`${field.name} must be greater than 0`);
          }
        }
        values[fieldType] = value;
        if (field.required && !value) {
          validationErrors.push(`${field.name} is required`);
        } else if (field.partOfDuplicationKey && !value) {
          validationErrors.push(`${field.name} is required because it is part of the duplication key`);
        }

        if (field.allowedValues && value) {
          const allowedValues = field.allowedValues.split("|").map(v => v.trim());
          if (!allowedValues.includes(value)) {
            validationErrors.push(`${field.name} must be one of: ${allowedValues.join(", ")}`);
          }
        }

        if (fieldType === "DUPLICATION_KEY" && value) {
          if (seenDuplicationKeys.has(value)) {
            validationErrors.push("Duplicate duplication key in upload file");
          } else {
            seenDuplicationKeys.add(value);
          }
        }
      });

      return {
        rowNumber: row.rowNumber,
        values,
        validationErrors
      };
    },
    async processPayouts() {
      if (!this.canProcessPayouts) return;
      this.errors = {};
      this.isUploading = true;
      try {
        let uploadId = null;
        if (this.selectedTemplate?.storeUpload && this.selectedFile) {
          const formData = new FormData();
          formData.append("file", this.selectedFile);
          formData.append("meta", "BULK_PAYOUT");
          const uploadRes = await ApiService.post("/api/uploads", formData);
          uploadId = uploadRes.data.id;
        }

        const { data } = await ApiService.post("/api/bulkPayouts/process", {
          bulkPayoutTemplateKey: this.selectedTemplate.bulkPayoutTemplateKey,
          awardVehicleId: this.selectedAwardVehicle.id,
          payoutRelation: this.derivedPayoutRelation,
          uploadId: uploadId,
          rows: this.parsedRecords.map(record => ({
            rowNumber: record.rowNumber,
            values: record.values
          }))
        });
        this.processResult = {
          bulkPayoutBatchId: data.bulkPayoutBatchId || null,
          totalRows: data.totalRows || 0,
          successfulRows: data.successfulRows || 0,
          failedRows: data.failedRows || 0
        };
        this.showSummary = true;
        this.currentStep = 3;
      } catch (error) {
        this.errors = this.$api.getErrorsFromResponse(error);
      } finally {
        this.isUploading = false;
      }
    },
    backToSetup() {
      this.isUploading = false;
      this.currentStep = 1;
      this.selectedFile = null;
      this.parsedRecords = [];
      this.filter = "";
      this.errors = {};
      this._seenDuplicationKeys = new Set();
    },
    onStartOver() {
      this.backToSetup();
      this.payoutType = null;
      this.selectedParticipantType = null;
      this.selectedOrganizationType = null;
      this.selectedTemplate = null;
      this.inactiveTemplate = false;
      this.selectedAwardVehicle = null;
      this.showSummary = false;
      this.processResult = { bulkPayoutBatchId: null, totalRows: 0, successfulRows: 0, failedRows: 0 };
    }
  },
  watch: {
    payoutType() {
      this.selectedParticipantType = null;
      this.selectedOrganizationType = null;
      this.selectedTemplate = null;
      this.selectedAwardVehicle = null;
      this.selectedFile = null;
      this.parsedRecords = [];
      this.filter = "";
      this.currentStep = 1;
    },
    selectedParticipantType() {
      if (this.payoutType === "PARTICIPANT") {
        this.fetchAssignedTemplate();
        this.selectedAwardVehicle = null;
        this.selectedFile = null;
        this.parsedRecords = [];
        this.filter = "";
      }
    },
    selectedOrganizationType() {
      if (this.payoutType === "ORGANIZATION") {
        this.fetchAssignedTemplate();
        this.selectedAwardVehicle = null;
        this.selectedFile = null;
        this.parsedRecords = [];
        this.filter = "";
      }
    },
    selectedTemplate() {
      this.selectedFile = null;
      this.parsedRecords = [];
      this.filter = "";
      this._seenDuplicationKeys = new Set();
    },
    availableAwardVehicles(vehicles) {
      const selectable = this.getSelectableAwardVehicles(vehicles);
      if (selectable.length === 1) {
        this.selectedAwardVehicle = selectable[0];
      }
    },
    selectedAwardVehicle() {
      this.selectedFile = null;
      this.parsedRecords = [];
      this.filter = "";
    },
    currentStep(step) {
      if (step === 2 && !this.canOpenStep2) {
        this.currentStep = 1;
      }
      if (step === 3 && !this.showSummary) {
        this.currentStep = this.canOpenStep2 ? 2 : 1;
      }
    }
  }
};
</script>

<style scoped>
.bulk-payout-step1-actions .v-btn + .v-btn {
  margin-left: 8px;
}
.bulk-payout-preview-table ::v-deep td:last-child {
  white-space: normal;
  word-break: break-word;
  vertical-align: top;
}
</style>
